import { DriverStatus } from "../../types/enums";

export type MuiColor = "success" | "error" | "warning" | "primary" | "info" | undefined;

export enum DocStatus {
  MISSING = "MISSING",
  PENDING = "PENDING",
  VERIFIED = "VERIFIED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED"
}

export type Driver = {
  id: number;
  userUid: string;
  licenseNumber?: string;
  vehicleDetails?: string;
  carName?: string;
  vehicleType?: string;
  vehicleColor?: string;
  vehicleRcDetails?: string;
  vehicleLicensePlateNumber?: string;
  isAvailable: boolean;
  rating: number;
  isDeleted: boolean;
  createdAt?: string;
  updatedAt?: string;
  isRCAvailable: boolean;
  isRCVerified: boolean;
  rcStatus?: DocStatus;
  isDLAvailable: boolean;
  isDLVerified: boolean;
  dlStatus?: DocStatus;
  isProfilePhotoAvailable: boolean;
  isProfilePhotoVerified: boolean;
  mandatoryPermissions?: any;
  isTermsAndConditionAccepted: boolean;
  cImageUrl?: string | string[];
  dlImageUrl?: string | string[];
  profilePhotoUrl?: string;
  status: DriverStatus;
  insuranceNumber?: string;
  insuranceUrls?: string[];
  insuranceStatus?: DocStatus;
  pucNumber?: string;
  pucUrls?: string[];
  pucStatus?: DocStatus;
  statePermitNumber?: string;
  statePermitUrls?: string[];
  statePermitStatus?: DocStatus;
  aitpNumber?: string;
  aitpUrls?: string[];
  aitpStatus?: DocStatus;
  enrolledById?: number | null;
  enrolledAt?: string | null;
  approvedById?: number | null;
  approvedAt?: string | null;
  enrolledBy?: {
    firstName: string;
    lastName: string;
    role: string;
  } | null;
  approvedBy?: {
    firstName: string;
    lastName: string;
    role: string;
  } | null;
  user?: {
    name?: number;
    phone: string;
    appType?: string;
    currentCity?: string;
    homeCity?: string;
    suspended?: boolean;
    // Android versionCode reported on register-fcm-token. null = build predates version reporting,
    // i.e. the cohort excluded from the location_ping rails.
    appVersion?: number | null;       // legacy shared column — do NOT display, last-writer-wins
    driverAppVersion?: number | null; // what the location_ping gate reads
    riderAppVersion?: number | null;
    // Stamped by the same call, so it doubles as "when we last heard from this app version".
    fcmTokenUpdatedAt?: string | null;
  };
};

/** Shape of the approve/compliance modal working form (signed URLs resolved). */
export type VerifiedForm = {
  id: number | null;
  licenseNumber: string;
  vehicleLicensePlateNumber: string;
  isProfilePhotoVerified: boolean;
  isAvailable: boolean;
  rcStatus: DocStatus;
  dlStatus: DocStatus;
  cImageUrl: string[];
  dlImageUrl: string[];
  profilePhotoUrl: string[];
  insuranceNumber: string;
  insuranceStatus: DocStatus;
  insuranceUrls: string[];
  pucNumber: string;
  pucStatus: DocStatus;
  pucUrls: string[];
  statePermitNumber: string;
  statePermitStatus: DocStatus;
  statePermitUrls: string[];
  aitpNumber: string;
  aitpStatus: DocStatus;
  aitpUrls: string[];
  isDLAvailable: boolean;
  isRCAvailable: boolean;
  isProfilePhotoAvailable: boolean;
  isTermsAndConditionAccepted: boolean;
  mandatoryPermissions: any;
};

export const booleanFields = [
  "isAvailable", "isDeleted", "isRCAvailable", "isRCVerified",
  "isDLAvailable", "isDLVerified", "isProfilePhotoAvailable", "isProfilePhotoVerified", "isTermsAndConditionAccepted"
] as const;

export const allFields = [
  "licenseNumber", "vehicleDetails", "carName", "vehicleType", "vehicleColor", "vehicleRcDetails",
  "vehicleLicensePlateNumber", "rating", "mandatoryPermissions", "cImageUrl", "dlImageUrl", "profilePhotoUrl",
  "status",
  "insuranceNumber", "pucNumber", "statePermitNumber", "aitpNumber",
  ...booleanFields
] as const;

/** Initial/empty approve-modal form. */
export const emptyVerifiedForm: VerifiedForm = {
  id: null,
  licenseNumber: "",
  vehicleLicensePlateNumber: "",
  isProfilePhotoVerified: false,
  isAvailable: false,
  rcStatus: DocStatus.MISSING,
  dlStatus: DocStatus.MISSING,
  cImageUrl: [],
  dlImageUrl: [],
  profilePhotoUrl: [],
  insuranceNumber: "",
  insuranceStatus: DocStatus.MISSING,
  insuranceUrls: [],
  pucNumber: "",
  pucStatus: DocStatus.MISSING,
  pucUrls: [],
  statePermitNumber: "",
  statePermitStatus: DocStatus.MISSING,
  statePermitUrls: [],
  aitpNumber: "",
  aitpStatus: DocStatus.MISSING,
  aitpUrls: [],
  isDLAvailable: false,
  isRCAvailable: false,
  isProfilePhotoAvailable: false,
  isTermsAndConditionAccepted: false,
  mandatoryPermissions: {}
};

export const getStatusColor = (status: DriverStatus): MuiColor => {
  switch (status) {
    case DriverStatus.ONLINE:
      return "success";
    case DriverStatus.SILENT:
      return "warning";
    case DriverStatus.OFFLINE:
      return "error";
    default:
      return "info";
  }
};
